// The Skill Builder player.
//
// A Skill Builder is an AcroForm PDF with an embedded answer-key.json. The
// student types into the real form fields, and the typed values are read out of
// pdfjs's annotationStorage and marked server-side against the key inside the
// PDF. The bytes the student filled in are never what gets marked.
//
// Adapted from the interactive worksheet player on new-functions/pdf-form-fill.
// Two differences worth knowing:
//
//  - The URL and the readability check come from ONE call to the server
//    (`/studentBmd/skillbuilder`) rather than being assembled here. A Skill
//    Builder lives in its own table under its own S3 prefix, and having the
//    client build that path was how the worksheet version acquired a hardcoded
//    bucket string.
//  - The result is shown immediately. Marking returns the score and the
//    verdict, so there is no reason to reload the page and make the student go
//    looking for it.
//
// The flattened PDF is sent alongside the answers as the tutor's review copy.
// It is OPTIONAL at every step: if the browser cannot produce it, or the upload
// fails, the answers are still submitted and still marked.

import PropTypes from "prop-types"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Button, Container, Modal, ModalBody, ModalFooter } from "reactstrap"
import { Document, Page, pdfjs } from "react-pdf"
import "react-pdf/dist/esm/Page/AnnotationLayer.css"
import "react-pdf/dist/esm/Page/TextLayer.css"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import Loader from "react-spinner-loader"

import duration from "../../assets/images/services-icon/duration.png"

import {
  openSkillbuilder,
  submitSkillbuilder,
  fetchSkillbuilderPdf,
} from "../../modules/skillbuilder/skillbuilder.service"

// react-pdf 7.7.x bundles its own pdfjs — pin the worker to the SAME version.
// Matches worksheet.js.
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

const SkillBuilder = ({ onFinished }) => {
  // The lesson activity key comes from the URL, and everything else the submit
  // needs comes back from the open call, so this page needs nothing handed to
  // it by whatever navigated here.
  const { key: activityKey } = useParams()
  const navigate = useNavigate()
  const authUser = JSON.parse(localStorage.getItem("authStudentUser")) || null

  // The PDF BYTES, fetched through the API rather than straight from the asset
  // store: a direct fetch needs bucket CORS and is hijacked by download manager
  // extensions, which fails with nothing useful in the console.
  const [pdfData, setPdfData] = useState(null)
  const [context, setContext] = useState(null)
  // The heading. The SKILL name, not skillbuilder_name, which is an S3 key.
  const [title, setTitle] = useState("Skill Builder")
  const [numPages, setNumPages] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  // Set when the answer key cannot be read: the worksheet is never opened.
  const [blocked, setBlocked] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [result, setResult] = useState(null)
  const [seconds, setSeconds] = useState(0)

  const pdfProxyRef = useRef(null)

  // A new object identity here makes react-pdf reload the document, so it is
  // built once per set of bytes rather than on every render.
  const pdfFile = useMemo(() => (pdfData ? { data: pdfData } : null), [pdfData])

  // --- open -------------------------------------------------------------
  useEffect(() => {
    let cancelled = false
    if (!activityKey || !authUser) return undefined

    setLoading(true)
    setError("")
    setBlocked(null)

    openSkillbuilder(authUser, activityKey)
      .then(async data => {
        if (cancelled) return
        if (!data || data.status !== 1) {
          setError("This skill builder could not be opened.")
        } else if (!data.can_be_marked) {
          // Opening it would waste the student's time: nothing they typed
          // could be marked.
          setBlocked({ code: data.code, message: data.message })
        } else {
          setContext({
            fk_lesson_key: data.fk_lesson_key,
            fk_ses_key: data.fk_ses_key,
            fk_sub_id: data.fk_sub_id,
          })
          if (data.skill_name) setTitle(data.skill_name)
          // A worksheet already submitted opens as a REVIEW: the server sends
          // back the marked copy, and the score it was given at the time. It is
          // never a fresh attempt, however the student got here.
          if (data.already_submitted && data.attempt) {
            setResult({
              score: data.attempt.score,
              passed: data.attempt.passed,
              pass_percent: data.attempt.pass_percent,
              questions: data.attempt.questions || [],
              reopened: true,
            })
          }
          const bytes = await fetchSkillbuilderPdf(authUser, activityKey)
          if (!cancelled) setPdfData(bytes)
        }
        setLoading(false)
      })
      .catch(err => {
        if (cancelled) return
        console.error("Could not open skill builder:", err)
        setError("This skill builder could not be opened.")
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [activityKey, authUser])

  // --- the stopwatch that becomes submitted_on --------------------------
  useEffect(() => {
    if (loading || blocked || result) return undefined
    const timer = setInterval(() => setSeconds(s => s + 1), 1000)
    return () => clearInterval(timer)
  }, [loading, blocked, result])

  const onDocumentLoad = useCallback(pdf => {
    pdfProxyRef.current = pdf
    setNumPages(pdf.numPages)
  }, [])

  /**
   * The typed values, keyed by FIELD NAME, which is what the answer key names.
   *
   * pdfjs keys annotationStorage by annotation id, not by field name, and one
   * field can own several annotations, so the field objects have to be walked
   * to translate. An untouched field yields "" rather than being omitted, so
   * the server marks it wrong instead of skipping it.
   */
  const readTypedValues = async () => {
    const pdf = pdfProxyRef.current
    if (!pdf) return {}
    const fieldObjects = (await pdf.getFieldObjects()) || {}
    const storage = pdf.annotationStorage.getAll() || {}
    const out = {}
    for (const [name, annots] of Object.entries(fieldObjects)) {
      let value = ""
      for (const a of annots) {
        const stored = storage[a.id]
        if (stored && stored.value !== undefined && stored.value !== "") {
          value = stored.value
          break
        }
        if (a.value) value = a.value
      }
      out[name] = value
    }
    return out
  }

  /**
   * The filled-in PDF, for the tutor to look at.
   *
   * pdfjs writes an incremental update, which pdf-lib cannot reliably read
   * back. That is fine: nothing reads this file programmatically. The score
   * comes from the typed values and this copy is only ever opened by a person.
   *
   * Returns null on failure rather than throwing - losing the review copy must
   * not lose the student's answers.
   */
  const saveFilledPdf = async () => {
    const pdf = pdfProxyRef.current
    if (!pdf || typeof pdf.saveDocument !== "function") return null
    try {
      const bytes = await pdf.saveDocument()
      return new Blob([bytes], { type: "application/pdf" })
    } catch (err) {
      console.error("Could not save the filled skill builder PDF", err)
      return null
    }
  }

  const handleSubmit = async () => {
    setConfirmOpen(false)
    setSubmitting(true)
    try {
      // Read the typed values BEFORE saving the document: saving can reset the
      // annotation storage on some pdfjs builds.
      const answers = await readTypedValues()
      const filledPdf = await saveFilledPdf()
      const data = await submitSkillbuilder(authUser, {
        pk_lesson_activity_key: activityKey,
        fk_lesson_key: context && context.fk_lesson_key,
        fk_ses_key: context && context.fk_ses_key,
        fk_sub_id: context && context.fk_sub_id,
        question_id: null,
        seconds_spent: seconds,
        answers,
        filledPdf,
      })

      if (!data || data.status !== 1) {
        toast.error("Connection to server lost! Kindly inform admin", {
          autoClose: 3000,
        })
        setSubmitting(false)
        return
      }

      setResult(data)
      setSubmitting(false)

      // Swap the worksheet for the marked copy: the student's own answers with
      // a tick or a cross on each box. Rendering the blank one they were just
      // typing into is what made a submitted worksheet look empty.
      try {
        const marked = await fetchSkillbuilderPdf(authUser, activityKey)
        setPdfData(marked)
      } catch (err) {
        // The score is already on screen, so this is a poorer review, not a
        // lost one.
        console.error("Could not load the marked copy:", err)
      }

      if (typeof onFinished === "function") onFinished(data)
    } catch (err) {
      console.error("Error submitting skill builder:", err)
      // 422 means the server refused to mark it - most likely the answer key
      // stopped being readable between opening and submitting. NOTHING was
      // saved, so the answers stay on screen and can be submitted again.
      if (err?.response?.status === 422) {
        // 422 covers two different refusals: a key that stopped being readable,
        // and a worksheet that was already submitted. The server says which, so
        // its message is preferred over guessing here.
        const serverSaid = err?.response?.data?.message
        toast.error(
          serverSaid ||
            "This skill builder cannot be marked right now. Your answers have been kept - please tell your tutor.",
          { autoClose: 6000 }
        )
        // Already submitted means there IS a result to show, so the page should
        // become the review rather than sit there offering Submit again.
        if (/already been submitted/i.test(serverSaid || "")) {
          window.location.reload()
        }
      } else {
        toast.error("Could not submit. Please try again.", { autoClose: 3000 })
      }
      setSubmitting(false)
    }
  }

  // --- render -----------------------------------------------------------

  /** mm:ss, matching the interactive worksheet player's clock. */
  const formatTime = total => {
    const mins = String(Math.floor(total / 60)).padStart(2, "0")
    const secs = String(total % 60).padStart(2, "0")
    return `${mins}:${secs}`
  }

  const goBack = () => navigate(-1)

  // Once marked, the form is flattened: the answers are already scored, so
  // letting the student keep typing would show them edits that count for
  // nothing. This is also what drives `renderForms` below.
  const submitted = !!result

  // The correct answers are deliberately NOT listed on this screen. The same
  // Skill Builder is re-assigned after a tutor has gone through it, so showing
  // a student the answer sheet would hand them the re-attempt. They still see
  // which boxes were right and wrong: the marked copy below carries a tick or a
  // cross on every one.


  const body = () => {
    if (blocked) {
      // A worksheet the server could not FETCH is a different thing from one it
      // read and found unmarkable. The first is usually momentary and clears by
      // itself, so telling the student the content is broken sends them to
      // their tutor over something that fixes itself.
      const couldNotReach = blocked.code === "FETCH_FAILED"
      return (
        <div className="text-center p-5">
          <p className="font-size-18 fw-bold text-dark mb-2">
            {couldNotReach
              ? "This skill builder could not be opened"
              : "This skill builder is not ready yet"}
          </p>
          <p className="font-size-16 text-muted mb-1">
            {couldNotReach
              ? "It could not be downloaded just now. Please go back and try again in a moment."
              : "Nothing you write on it could be marked, so it has not been opened."}
          </p>
          <p className="font-size-16 text-muted mb-0">
            {couldNotReach
              ? "If it keeps happening, kindly inform your tutor"
              : "Kindly inform your tutor"}
          </p>
        </div>
      )
    }

    if (error) {
      return <div className="text-danger font-size-16 p-4 text-center">{error}</div>
    }

    if (!pdfFile) return null

    return (
      <div className="d-flex flex-column align-items-center pb-100">
        {result ? (
          <div className="text-center mb-4">
            {result.reopened ? (
              <p className="text-muted font-size-14 mb-1">
                You have already done this one. Here is how it went.
              </p>
            ) : null}
            <h2 className={result.passed ? "text-success" : "text-warning"}>
              {result.score.percent}%
            </h2>
            {/* The unit comes from the server because the total depends on how
                it counted: whole questions under PER_GROUP, single boxes under
                PER_BOX. "4 out of 6" means different things in each, so the
                word is not something this page can assume. */}
            <p className="mb-1 font-size-16">
              {result.score.correct} out of {result.score.total}{" "}
              {result.score.unit === "questions" ? "questions" : "answers"}{" "}
              correct
            </p>
            {/* Under group marking, a single slip costs a whole question, so
                the box tally is shown too or the score looks wrong to a student
                who can see more ticks than that. */}
            {result.score.unit === "questions" && result.score.per_box ? (
              <p className="text-muted font-size-14 mb-1">
                {result.score.per_box.correct} of{" "}
                {result.score.per_box.total} answer boxes were right. A question
                counts only when every box in it is.
              </p>
            ) : null}
            {result.passed ? (
              <p className="text-success fw-bold font-size-16 mb-2">
                Well done, you have passed this one.
              </p>
            ) : (
              <p className="text-muted font-size-16 mb-2">
                You need {result.pass_percent}% to pass this one. Your tutor
                will go through it with you, and then you can try it again.
              </p>
            )}

          </div>
        ) : null}

        <Document
          file={pdfFile}
          onLoadSuccess={onDocumentLoad}
          onLoadError={err => {
            console.error("Could not load skill builder PDF:", err)
            setError("This skill builder could not be opened.")
          }}
          loading="Loading skill builder…"
        >
          {Array.from({ length: numPages }, (_, i) => (
            <div key={`sb_page_${i + 1}`} className="mb-3 shadow-sm bg-white">
              <Page
                pageNumber={i + 1}
                /* renderForms is what makes the boxes TYPEABLE. react-pdf 7
                   renamed it from renderInteractiveForms; the old name is
                   silently ignored, which renders the fields as flat
                   rectangles that look right and cannot be typed into. */
                renderForms={!submitted}
                renderAnnotationLayer
                renderTextLayer={false}
                scale={1.4}
              />
            </div>
          ))}
        </Document>
      </div>
    )
  }

  return (
    <React.Fragment>
      <Loader show={loading} type="body" />
      <ToastContainer autoClose={3000} />

      <Modal isOpen={confirmOpen} toggle={() => setConfirmOpen(!confirmOpen)}>
        <ModalBody>
          <p className="text-center fw-bold text-dark mb-0 font-size-18">
            Once submitted, you will not be able to change your answers!
          </p>
        </ModalBody>
        <ModalFooter className="justify-content-center">
          <Button
            className="btn-cst-danger-md"
            onClick={() => setConfirmOpen(false)}
          >
            CANCEL
          </Button>
          <Button
            className="btn-cst-success-md"
            disabled={submitting}
            onClick={handleSubmit}
          >
            CONFIRM
          </Button>
        </ModalFooter>
      </Modal>

      <div className="page-wrapper">
        <div
          style={{
            zIndex: 1000,
            position: "fixed",
            top: 0,
            width: "100%",
            transition: "top 0.3s ease-in-out",
          }}
        >
          <div className="cs-pg-header bg-dark-blue py-4">
            <Container className="custom-wt">
              <div className="d-flex align-items-center">
                <div
                  className="mt-0 border-right me-4"
                  style={{ width: "20%" }}
                >
                  <div
                    className="text-white font-size-14 text-uppercase cursor-pointer"
                    onClick={goBack}
                  >
                    <span>
                      <i className="fas fa-chevron-left me-1"></i>
                    </span>{" "}
                    Back
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between flex-grow-1">
                  <h6 className="mb-0 font-size-20 text-white w-60 fw-regular">
                    {title}
                  </h6>
                  <div className="d-flex align-items-center mb-1">
                    {!blocked && !error && !submitted ? (
                      <div className="custom-timer2 d-flex align-items-center mt-0 me-3">
                        <img width={24} className="me-2" src={duration} alt="" />
                        <h1
                          className="text-end mb-0 mt-1"
                          style={{ fontSize: "28px" }}
                        >
                          {formatTime(seconds)}
                        </h1>
                      </div>
                    ) : null}
                    {blocked || error ? null : !submitted ? (
                      <Button
                        className="btn btn-danger cs-btn-danger text-uppercase"
                        disabled={submitting || !numPages}
                        onClick={() => setConfirmOpen(true)}
                      >
                        {submitting ? "SUBMITTING…" : "SUBMIT"}
                      </Button>
                    ) : (
                      <span className="text-white font-size-14">Submitted</span>
                    )}
                  </div>
                </div>
              </div>
            </Container>
          </div>
        </div>

        <div
          className="assessement-section worksheet-width-hold"
          style={{ marginTop: "180px" }}
        >
          {body()}
        </div>
      </div>
    </React.Fragment>
  )
}

SkillBuilder.propTypes = {
  onFinished: PropTypes.func,
}

export default SkillBuilder
